import FormikExtended from '@sprinx/react-forms/FormikExtended';
import WrapDialogForm from '@sprinx/react-mui-components/WrapDialogForm';
import PriceField from '@sprinx/react-mui-fields/PriceField';
import TextField from '@sprinx/react-mui-fields/TextField';
import React from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { apiClientState, currencyState } from '../../../api/appState';
import { enqueueSnackbarMessage } from '../../../api/snackbars';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import CustomerProfilePageDeliveryPlaceSelector from './CustomerProfilePageDeliveryPlaceSelector';
import CustomerProfilePageUserSupervisorSelector from './CustomerProfilePageUserSupervisorSelector';
import { SidiaryUser } from './useSubsidiaryUsersList';

export interface CustomerProfilePageUserEditDialogProps {
  customerId: string;
  isMaster?: boolean;
  onClose: () => void;
  onFormReleased: (p: any) => any;
  onSaved: () => void;
  open: boolean;
  userDetails: SidiaryUser;
}

export interface CustomerProfileEdit {
  deliveryPlaces: Array<string> | undefined;
  name: string;
  // email: string;
  orderTotalLimit: number | undefined;
  phone: string;
  supervisor: string | undefined;
  userId: string;
}
export interface CustomerProfileEditErrors {
  name?: string;
  orderTotalLimit?: string;
  // email?: string;
  phone?: string;
  supervisor?: string;
}

export interface CustomerProfileEditTouched {
  // email?: boolean;
  name?: boolean;
  orderTotalLimit?: boolean;
  phone?: boolean;
  supervisor?: boolean;
}

function useUserSetDetailsHandler(): (params: CustomerProfileEdit) => Promise<void> {
  const apiClient = useRecoilValue(apiClientState);
  return React.useCallback(
    (params: CustomerProfileEdit) => apiClient.post<void, CustomerProfileEdit>('/v1/users/update-subsidiary', params),
    [apiClient],
  );
}

const CustomerProfilePageUserEditDialog: React.FC<CustomerProfilePageUserEditDialogProps> = ({
  customerId,
  isMaster,
  userDetails,
  open,
  onClose,
  onFormReleased,
  onSaved: handleSaved,
}) => {
  const t = useTranslateWithFallback();
  const currency = useRecoilValue(currencyState);
  const setUserDetails = useUserSetDetailsHandler();
  const showMessage = useSetRecoilState(enqueueSnackbarMessage);
  const handleSave = React.useCallback(
    async ({ userId, name, supervisor, orderTotalLimit, deliveryPlaces, phone }: CustomerProfileEdit) => {
      if (userId && name) {
        setUserDetails({ userId, name, supervisor, orderTotalLimit, deliveryPlaces, phone })
          .then(() => {
            handleSaved();
            showMessage({ message: t('customerProfilePage/changeData/messages/success'), variant: 'success' });
          })
          .catch(() => {
            showMessage({ message: t('customerProfilePage/changeData/messages/error'), variant: 'error' });
          });
      }
    },
    [setUserDetails, handleSaved, showMessage, t],
  );

  return (
    <FormikExtended<CustomerProfileEdit, CustomerProfileEditErrors, CustomerProfileEditTouched>
      onFormReleased={onFormReleased}
      onSave={handleSave}
      initialValues={{
        userId: userDetails.id,
        name: userDetails.name,
        phone: userDetails.phone ?? '',
        supervisor: userDetails.supervisor,
        orderTotalLimit: userDetails.orderTotalLimit,
        deliveryPlaces: userDetails.deliveryPlaces || [customerId],
        // email: userDetails.email,
      }}
      validationFastest
      validationSchema={{
        userId: { type: 'string', empty: false },
        phone: { type: 'string', empty: false },
        // email: { type: 'email', empty: false },
        name: { type: 'string', empty: false },
      }}
      render={(formikProps) => (
        <WrapDialogForm
          {...formikProps}
          dialogTitle={t('customerProfilePage/changeData/dialogTitle')}
          open={open}
          onClose={onClose}
          dialogProps={{
            fullWidth: true,
            maxWidth: 'sm',
          }}
          render={() => {
            const { errors, onChange: handleChange, values } = formikProps;
            return (
              <>
                <TextField
                  label={t('common/name')}
                  disabled={isMaster}
                  helperText={''}
                  value={values.name}
                  error={errors.name}
                  onChange={handleChange('name')}
                  fullWidth
                />
                <TextField
                  label={t('common/phone')}
                  onChange={handleChange('phone')}
                  value={values.phone}
                  error={errors.phone}
                  fullWidth
                  margin='normal'
                />

                <PriceField
                  currency={currency}
                  label={t('orders/order/priceLimit')}
                  helperText={''}
                  value={values.orderTotalLimit}
                  error={errors.orderTotalLimit}
                  onChange={handleChange('orderTotalLimit')}
                  fullWidth
                />
                <CustomerProfilePageUserSupervisorSelector
                  fullWidth
                  label={t('common/approver')}
                  customerId={customerId}
                  userId={userDetails.id}
                  value={values.supervisor}
                  onChange={handleChange('supervisor')}
                />
                {!isMaster && (
                  <CustomerProfilePageDeliveryPlaceSelector
                    value={values.deliveryPlaces || []}
                    onChange={handleChange('deliveryPlaces')}
                  />
                )}
                {/* <TextField
                  label={'E-mail'}
                  helperText={''}
                  value={values.email}
                  error={errors.email}
                  onChange={handleChange('email')}
                  fullWidth
                /> */}
              </>
            );
          }}
        />
      )}
    />
  );
};

export default CustomerProfilePageUserEditDialog;
